import { storeToRefs } from "pinia";
import { helpers, required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import ApiService from "@services/ApiService";
import { useDepositStore } from "@stores/deposit";
import { useSettingsStore } from "@stores/settings";
import { useCouponStore } from "@stores/coupon";
import { useAuthStore } from "@stores/auth";
import { useAlert } from "@stores/alert";
import { useTrackingStore } from "@stores/tracking";
import { useLegitimuzStatusStore } from "@stores/legitimuz-status";
import { useUiStore } from "@stores/ui";
import { useLegitimuzVerification } from "@/composables/base/useLegitimuzVerification";


export default function useDeposit() {
  const depositStore = useDepositStore();
  const settingsStore = useSettingsStore();
  const couponStore = useCouponStore();
  const authStore = useAuthStore();
  const { showAlert } = useAlert();
  const trackingStore = useTrackingStore();

  const image = ref();
  let loading = ref(false);
  let amount = ref();
  let link = ref();
  let depositSection = ref(0);
  const { transactionId } = storeToRefs(depositStore);
  const isCountdownActive = ref(settingsStore.settings?.deposit_popup_timer);

  // Deposit code
  const depositId = ref("");
  let showIcon = ref(0);
  let depositData = ref("");

  const inputAmount = ref("R$ 0,00");

  let formError = "";

  const rules = {
    amount: {
      required: helpers.withMessage("Campo obrigatorio", required)
    }
  };

  const modalBanner = computed(() => {
    if (!settingsStore.state.modalBanners?.deposit) return false;

    const depositBanners = settingsStore.state.modalBanners?.deposit;
    if (!Array.isArray(depositBanners) || depositBanners.length === 0) return false;
    return depositBanners[0]?.src || false;
  });

  const newDeposit = () => {
    couponStore.clearCoupon();

    depositId.value = "";
    depositData.value = "";
    image.value = null;
    link.value = null;
    amount.value = null;
    inputAmount.value = "R$ 0,00";
    depositSection.value = 0;
    formError = "";
    isCountdownActive.value = settingsStore.settings?.deposit_popup_timer;
  };

  function convertToInteger(amount: any) {
    return parseInt(amount.replace(/[\D]/g, ""));
  }

  const v$ = useVuelidate(rules, { amount });

  async function fetchPayment(transactionId: any) {
    loading.value = true;
    const { data } = await ApiService.get(`/api/deposits/${transactionId}`);
    depositData.value = data;
    loading.value = false;
    if (data.status === 2) {
      depositStore.isModalOpen = false;
      showAlert("success", "Depósito aprovado com sucesso.");
      await authStore.getUser();
    }

    // Here we send the deposit_pending event
    await trackingStore.actions.sendEvent("deposit", {
      id: transactionId,
      amount: Number(amount.value.replace(/\D/g, "")) / 100,
      currency: "BRL", // @TODO: - If we implement multi-language one day, this line has to be dynamic
      status: "pending"
    });


    /**
     * Testing approved deposit event
     * @TODO - Remove that after testing
     */
    // setTimeout(async () => {
    //   await trackingStore.actions.sendEvent("deposit_approved", {
    //     amount: Number(amount.value.replace(/\D/g, "")) / 100
    //   });
    // }, 2000);

  }

  const post = async () => {
    if (loading.value) {
      console.log("Request already in progress, preventing duplicate submission");
      return;
    }

    loading.value = true;

    try {
      // Check if user has CPF first
      if (!authStore.user?.document?.number) {
        authStore.toggleUserDocumentModal();
        loading.value = false; // Reset loading before returning
        
        // Listen for document completion and continue flow
        const unwatch = watch(() => authStore.isUserDocumentModalOpen, (isOpen) => {
          if (!isOpen && authStore.user?.document?.number) {
            unwatch(); // Stop watching
            // Continue with the original flow
            post();
          }
        });
        
        return;
      }

      // Check legitimuz status if enabled and trigger verification directly
      const config = useRuntimeConfig();
      if (config.public.LEGITIMUZ_API_TOKEN) {
        const legitimuzStore = useLegitimuzStatusStore();
        const isApproved = await legitimuzStore.checkStatus(true);
        
        if (!isApproved) {
          // Trigger verification directly instead of showing modal
          const { triggerVerification, triggerFaceIndexVerification, initFaceIndex, userStatus } = useLegitimuzVerification();
          
          if (userStatus.value === 'pending_face_id') {
            await initFaceIndex();
            triggerFaceIndexVerification();
          } else {
            triggerVerification();
          }
          loading.value = false;
          return;
        }
      }

      // Apply coupon if input is not empty
      if (couponStore.couponModel && !couponStore.activatedCoupon) {
        const couponResponse = await couponStore.submitCouponCode(
          couponStore.couponModel
        );
        if (couponResponse?.error) {
          loading.value = false;
          return;
        }
      }

      await v$.value.$validate();

      if (!v$.value.$error) {
        let { data } = await ApiService.post("/api/deposits", {
          amount: Number(amount.value.replace(/\D/g, "")),
          params: useCookie("params").value,
          ...(couponStore.activatedCoupon.id && {
            user_coupon_id: couponStore.activatedCoupon?.id
          })
        });

        if (!data.success) {
          showAlert("error", data.message);
          formError = data.message;
          loading.value = false;
          return;
        }

        if (data.data?.message === "Unauthenticated.") {
          showAlert("error", "Sessão expirada, faça login novamente.");
          authStore.setLoggedIn(false);
          loading.value = false;
          return;
        }

        if (data.data.id) {
          console.log(data.data.id);
          depositId.value = data.data.id;
          depositSection.value = 1;
          await fetchPayment(depositId.value);
        }
      }
    } catch (error) {
      console.error("Error during deposit:", error);
      showAlert("error", "Ocorreu um erro ao processar o depósito.");
    } finally {
      loading.value = false;
    }
  };

  const formatInput = (event) => {
    amount.value = event.target.value.replace(/[^\d.,]/g, "");
  };

  const setAmount = (value: any) => {
    amount.value = value;
  };

  const setDepositAmount = (depositAmount: any) => {
    depositAmount = `R$ ${depositAmount},00`;
    const rawValue = depositAmount.replace(/[^\d.,]/g, "");
    const [integerPart, decimalPart] = rawValue.split(",");

    let formattedValue = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    if (decimalPart) {
      formattedValue += "," + decimalPart;
    }

    amount.value = rawValue;
    inputAmount.value = formattedValue;

    console.log("rawValue", rawValue);
    console.log("amount", amount.value);
    console.log("inputAmount", inputAmount.value);
  };

  const handleInputAmount = () => {

    let rawValue = Number(inputAmount.value.replaceAll("R$ ", "").replaceAll(".", "").replaceAll(",", ""));

    amount.value = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(rawValue / 100);

  };

  const copyPixCode = () => {
    showIcon.value = 1;

    const textToCopy = depositData.value.payment_code;
    const tempInput = document.createElement("input");
    tempInput.value = textToCopy;
    document.body.appendChild(tempInput);

    tempInput.select();
    try {
      const success = document.execCommand("copy");
      if (success) {
        // Success
        document.body.removeChild(tempInput);
        setTimeout(() => {
          showIcon.value = 0;
        }, 2000);
      } else {
        // Failure
        console.error("Unable to copy to clipboard");
        document.body.removeChild(tempInput);
        showIcon.value = 0;
      }
    } catch (err) {
      // Handle errors
      console.error("Unable to copy to clipboard", err);
      document.body.removeChild(tempInput);
      showIcon.value = 0;
    }
  };

  const transformSlotProps = (props) => {
    const formattedProps = {};

    Object.entries(props).forEach(([key, value]) => {
      formattedProps[key] = value < 10 ? `0${value}` : String(value);
    });

    return formattedProps;
  };

  const getImageSrc = (imageStr: string) => {
    if (imageStr.startsWith("http")) {
      return imageStr;
    }

    if (imageStr.startsWith("data:image")) {
      return imageStr;
    }

    return `data:image/png;base64,${imageStr}`;
  };

  watch(transactionId, async () => {
    if (transactionId) {
      depositSection.value = 1;
      isCountdownActive.value = false;
      depositId.value = depositStore.transactionId;
      await fetchPayment(depositStore.transactionId);
    }
  });

  return {
    modalBanner,
    image,
    loading,
    depositSection,
    isCountdownActive,
    depositId,
    showIcon,
    depositData,
    inputAmount,
    formError,
    newDeposit,
    fetchPayment,
    post,
    formatInput,
    setDepositAmount,
    copyPixCode,
    transformSlotProps,
    getImageSrc,
    setAmount,
    handleInputAmount
  };
}